import { useCallback, useMemo, type FC } from 'react';

import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import { DataTableVirtualized, Empty, EmptyHeader, EmptyTitle, useDataTable } from 'design-system';

import { symbolKey, type SymbolKey } from '@/entities/symbol';
import {
  symbolPreviewRegistry,
  type SymbolPreviewPresentation
} from '@/features/_shared/symbol-preview-registry';
import type { FeedSymbol } from '@/features/_shared/SymbolsPreview/api/fetchSymbolData';
import { symbolPreviewOptions } from '@/features/_shared/SymbolsPreview/api/symbolPreview.options';
import { usePeriodAbsFormatter } from '@/features/_shared/user';

import { getSymbolPreviewColumns, type SymbolPreviewTableRow } from './symbolPreviewColumns';

export type SymbolRowMetadata = {
  Title: string;
  Currency: string;
  Units: string;
  Alias?: string;
};

export type SymbolPreviewItem = {
  symbol: SymbolKey;
  label?: string;
  date?: string;
  periodAbs?: string;
  metadata?: SymbolRowMetadata;
};

export type SymbolsPreviewProps = {
  items: SymbolPreviewItem[];
  enableQuery?: boolean;
  forChat?: boolean;
  hideInfo?: boolean;
  presentation?: SymbolPreviewPresentation;
  stickyHeader?: boolean;
};

const ESTIMATED_ROW_HEIGHT = 24;
const MAX_NATURAL_ROWS = 12;
const NESTED_TABLE_CLASS_NAME = [
  'm-0 rounded-none border-0 bg-gx-tile-dark pl-2',
  '[&_[role=table]]:rounded-none',
  '[&_[role=rowgroup]:first-child>[role=row]]:border-b-0',
  '[&_[role=rowgroup]:first-child>[role=row]]:bg-gx-tile-dark',
  '[&_[role=columnheader]]:font-bold',
  '[&_[role=rowgroup]:last-child_[role=row]]:border-b-0',
  '[&_[role=rowgroup]:last-child_[role=row]]:bg-gx-tile-dark',
  '[&_[role=rowgroup]:last-child_[role=row]]:hover:bg-gx-tile-dark',
  '[&_[role=rowgroup]:last-child_[role=cell]]:h-6',
  '[&_[role=rowgroup]:last-child_[role=cell]]:py-0',
  '[&_[role=rowgroup]:last-child_[role=cell]]:text-muted-foreground'
].join(' ');

function getSymbolPreviewRowIds(items: SymbolPreviewItem[]) {
  const identityOccurrences = new Map<string, number>();

  return items.map(item => {
    const identity = JSON.stringify({
      symbol: symbolKey(item.symbol),
      label: item.label ?? null,
      date: item.date ?? 'today',
      periodAbs: item.periodAbs ?? null
    });
    const occurrence = identityOccurrences.get(identity) ?? 0;
    identityOccurrences.set(identity, occurrence + 1);

    // Exact duplicate items are indistinguishable through the public contract.
    // A per-identity occurrence keeps their row ids unique without coupling
    // unrelated rows to their array position.
    return `${identity}#${occurrence}`;
  });
}

function getTableClassName(
  forChat: boolean,
  stickyHeader: boolean,
  rowCount: number,
  presentation: NonNullable<SymbolsPreviewProps['presentation']>
) {
  const spacing = forChat ? 'mt-3 mb-0' : 'my-3';
  const chatBackground = forChat
    ? 'bg-gx-tile-dark [&_[role=table]]:bg-gx-tile-dark [&_[role=rowgroup]:first-child>[role=row]]:bg-gx-tile-dark'
    : '';
  const staticHeader = stickyHeader ? '' : '[&_[role=rowgroup]:first-child]:!static';
  // Feed, MiniTables, and MonthlyRecaps establish 650px as the market-widget
  // scroll bound. Twelve estimated rows plus the 40px header remain natural;
  // larger previews receive a definite viewport for effective windowing.
  const viewportHeight =
    rowCount > MAX_NATURAL_ROWS ? 'h-[650px] max-h-[650px]' : 'h-auto max-h-[650px]';

  if (presentation === 'nested') {
    return `${NESTED_TABLE_CLASS_NAME} ${staticHeader} ${viewportHeight}`.trim();
  }

  return `${spacing} ${chatBackground} ${staticHeader} ${viewportHeight}`.trim();
}

const SymbolsPreview: FC<SymbolsPreviewProps> = ({
  items,
  enableQuery = true,
  forChat = false,
  hideInfo = false,
  presentation = 'default',
  stickyHeader = true
}) => {
  const filteredItems = useMemo(() => items.filter(item => Boolean(item.symbol.Code)), [items]);
  const allSymbols = useMemo(() => filteredItems.map(item => item.symbol), [filteredItems]);
  const rowIds = useMemo(() => getSymbolPreviewRowIds(filteredItems), [filteredItems]);
  const queries = useMemo(
    () =>
      filteredItems.map(({ date = 'today', periodAbs, symbol }) => ({
        ...symbolPreviewOptions.bySymbol(symbol, date),
        enabled: enableQuery && !periodAbs
      })),
    [enableQuery, filteredItems]
  );

  const { formatPeriodAbs } = usePeriodAbsFormatter();
  const combineQueryResults = useCallback(
    (queryResults: UseQueryResult<FeedSymbol>[]): SymbolPreviewTableRow[] =>
      filteredItems.map(({ symbol, label, metadata, periodAbs }, index) => {
        const queryResult = queryResults[index];
        const data = queryResult?.data;
        const resolvedAlias = metadata?.Alias ?? data?.alias;

        return {
          id: rowIds[index],
          symbol,
          label,
          alias: resolvedAlias || symbol.Code,
          title: metadata?.Title ?? data?.title ?? '',
          periodAbs: formatPeriodAbs(periodAbs ?? data?.periodAbs),
          currency: metadata?.Currency ?? data?.currency ?? '',
          units: metadata?.Units ?? data?.units ?? '',
          loading: queryResult?.isLoading ?? false
        };
      }),
    [filteredItems, formatPeriodAbs, rowIds]
  );
  const rows = useQueries({
    queries,
    combine: combineQueryResults
  });

  const slots = symbolPreviewRegistry.getSlots();
  const columns = useMemo(
    () =>
      getSymbolPreviewColumns({
        allSymbols,
        forChat,
        hideInfo,
        presentation,
        slots
      }),
    [allSymbols, forChat, hideInfo, presentation, slots]
  );
  const table = useDataTable({
    columns,
    data: rows,
    getRowId: row => row.id
  });

  return (
    <DataTableVirtualized
      aria-label='Symbol preview'
      className={getTableClassName(forChat, stickyHeader, rows.length, presentation)}
      fitColumnsToViewport={presentation === 'nested'}
      emptyContent={
        <output
          aria-label='No symbols available.'
          className='block w-full'
        >
          <Empty>
            <EmptyHeader>
              <EmptyTitle>No symbols available.</EmptyTitle>
            </EmptyHeader>
          </Empty>
        </output>
      }
      estimateSize={ESTIMATED_ROW_HEIGHT}
      table={table}
    />
  );
};

export default SymbolsPreview;
